// F-WEB-018 /admin/categorias — tree 2 niveis + CRUD + RN08 enforcement.
// Redesign 2026-09-22 (W2): hierarquia com pai em --soft e filho indentado
// (handoff §9-12). Composto localmente (nao via DataTable) porque a lista
// e uma arvore, nao linhas planas — mesmo padrao responsivo do DataTable
// (tabela >=48rem / cartoes <48rem), replicado a mao pra poder diferenciar
// pai/filho visualmente linha a linha.
import { useState } from "react";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);
}
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronRight, Plus, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type { Category } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";

export function AdminCategoriesPage(): JSX.Element {
  const qc = useQueryClient();
  const { data: tree = [], isLoading, isError, error } = useQuery<Category[]>({
    queryKey: ["admin-categories"],
    queryFn: async () => (await api.get<Category[]>("/api/categories")).data,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);

  const delMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/categories/${id}`);
    },
    onSuccess: () => {
      toast.success("Categoria excluída");
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
    },
    onError: (err) => {
      // 409 in_use mostra mensagem amigável com count
      const msg = apiErrorMessage(err);
      toast.error(msg);
    },
  });

  return (
    <div>
      <PageHeader
        title="Categorias"
        description="Hierarquia canônica de 2 níveis (RN09). Reutilizadas por todos canais."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="icon" aria-hidden /> Nova categoria
          </Button>
        }
      />

      {isLoading ? (
        <Card>
          <CardContent className="stack">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      ) : isError ? (
        <EmptyState title="Erro" description={apiErrorMessage(error)} />
      ) : tree.length === 0 ? (
        <EmptyState
          title="Nenhuma categoria"
          description="Crie a primeira categoria para classificar produtos."
        />
      ) : (
        <Card flush>
          {/* Desktop: linhas com piso de coluna, pai em --soft */}
          <div className="hidden md:block">
            <div
              className="row row__head"
              style={{ gridTemplateColumns: "minmax(12rem,4fr) minmax(6rem,1fr) minmax(5rem,1fr) minmax(9rem,auto)" }}
            >
              <span className="row__cell">Nome</span>
              <span className="row__cell">Slug</span>
              <span className="row__cell">Status</span>
              <span className="row__cell" />
            </div>
            {tree.map((c) => (
              <CategoryRow
                key={c.id}
                cat={c}
                level={0}
                onEdit={setEditing}
                onDelete={(id) => {
                  if (confirm("Excluir esta categoria?")) delMut.mutate(id);
                }}
                delBusy={delMut.isPending}
              />
            ))}
          </div>

          {/* Mobile: cartoes, pai com destaque, filho indentado */}
          <div className="md:hidden stack" style={{ padding: ".75rem" }}>
            {tree.map((c) => (
              <CategoryCard
                key={c.id}
                cat={c}
                level={0}
                onEdit={setEditing}
                onDelete={(id) => {
                  if (confirm("Excluir esta categoria?")) delMut.mutate(id);
                }}
                delBusy={delMut.isPending}
              />
            ))}
          </div>
        </Card>
      )}

      <CategoryDialog
        key={editing?.id ?? "new"}
        open={createOpen || editing !== null}
        editing={editing}
        roots={tree}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function CategoryRow({
  cat,
  level,
  onEdit,
  onDelete,
  delBusy,
}: {
  cat: Category;
  level: number;
  onEdit: (c: Category) => void;
  onDelete: (id: number) => void;
  delBusy: boolean;
}): JSX.Element {
  return (
    <>
      <div
        className="row"
        style={{
          gridTemplateColumns: "minmax(12rem,4fr) minmax(6rem,1fr) minmax(5rem,1fr) minmax(9rem,auto)",
          padding: "0 1rem",
          background: level === 0 ? "var(--color-soft)" : undefined,
        }}
      >
        <span className="row__cell cluster" style={{ paddingLeft: `${level * 1.5}rem` }}>
          {level > 0 ? <ChevronRight className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden /> : null}
          <span className="row__title truncate">{cat.name}</span>
        </span>
        <span className="row__cell">
          <span className="chip chip--mono">{cat.slug_wc ?? "—"}</span>
        </span>
        <span className="row__cell">
          {cat.active ? <span className="chip chip--good">ativa</span> : <span className="chip chip--warn">inativa</span>}
        </span>
        <span className="row__cell cluster" style={{ justifyContent: "flex-end", gap: ".375rem" }}>
          <Button variant="ghost" size="sm" onClick={() => onEdit(cat)}>
            Editar
          </Button>
          <span aria-hidden style={{ color: "var(--color-ink-2)" }}>·</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(cat.id)}
            disabled={delBusy}
            title="Excluir"
            aria-label={`Excluir categoria ${cat.name}`}
          >
            Excluir
          </Button>
        </span>
      </div>
      {cat.children?.map((ch) => (
        <CategoryRow key={ch.id} cat={ch} level={level + 1} onEdit={onEdit} onDelete={onDelete} delBusy={delBusy} />
      ))}
    </>
  );
}

function CategoryCard({
  cat,
  level,
  onEdit,
  onDelete,
  delBusy,
}: {
  cat: Category;
  level: number;
  onEdit: (c: Category) => void;
  onDelete: (id: number) => void;
  delBusy: boolean;
}): JSX.Element {
  return (
    <>
      <div
        className="row-card"
        style={{
          marginLeft: `${level}rem`,
          background: level === 0 ? "var(--color-soft)" : "var(--color-card)",
        }}
      >
        <div className="row-card__body">
          <div className="cluster">
            {level > 0 ? <ChevronRight className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden /> : null}
            <span className="row__title truncate">{cat.name}</span>
          </div>
          <div className="cluster">
            <span className="chip chip--mono">{cat.slug_wc ?? "—"}</span>
            {cat.active ? <span className="chip chip--good">ativa</span> : <span className="chip chip--warn">inativa</span>}
          </div>
          <div className="cluster">
            <Button variant="secondary" size="sm" onClick={() => onEdit(cat)}>
              Editar
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(cat.id)}
              disabled={delBusy}
              aria-label="Excluir categoria"
            >
              <Trash2 className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden />
            </Button>
          </div>
        </div>
      </div>
      {cat.children?.map((ch) => (
        <CategoryCard key={ch.id} cat={ch} level={level + 1} onEdit={onEdit} onDelete={onDelete} delBusy={delBusy} />
      ))}
    </>
  );
}

function CategoryDialog({
  open,
  editing,
  roots,
  onOpenChange,
}: {
  open: boolean;
  editing: Category | null;
  roots: Category[];
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const isEdit = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [parentId, setParentId] = useState<string>(editing?.parent_id?.toString() ?? "");
  const [slug, setSlug] = useState(editing?.slug_wc ?? "");
  const [orderN, setOrderN] = useState<string>(editing?.order_n?.toString() ?? "0");
  const [active, setActive] = useState(editing?.active ?? true);
  // Slug acompanha nome ate user editar manualmente o campo slug.
  // Em modo edit: se slug atual ja diverge do slugify(nome), assume que foi tocado manualmente.
  const [slugTouched, setSlugTouched] = useState<boolean>(
    isEdit && editing ? (editing.slug_wc ?? "") !== slugify(editing.name ?? "") : false,
  );

  const mut = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name,
        parent_id: parentId ? Number(parentId) : null,
        active,
        order_n: orderN ? Number(orderN) : 0,
      };
      if (slug && slug.trim().length > 0) body.slug_wc = slug.trim();

      if (isEdit && editing) {
        await api.patch(`/api/categories/${editing.id}`, body);
      } else {
        await api.post("/api/categories", body);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? "Categoria atualizada" : "Categoria criada");
      qc.invalidateQueries({ queryKey: ["admin-categories"] });
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={editing?.id ?? "new"}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar categoria" : "Nova categoria"}</DialogTitle>
          <DialogDescription>
            Máximo 2 níveis. Se selecionar categoria pai, esta será subcategoria.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
          className="stack"
        >
          <div className="field">
            <Label htmlFor="cat-name">Nome</Label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => {
                const v = e.target.value;
                setName(v);
                if (!slugTouched) setSlug(slugify(v));
              }}
              required
              minLength={1}
            />
          </div>
          <div className="field">
            <Label htmlFor="cat-parent">Pai (nível raiz se vazio)</Label>
            <Select
              id="cat-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">(raiz)</option>
              {roots
                .filter((r) => !isEdit || r.id !== editing?.id)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </Select>
          </div>
          <div className="field">
            <Label htmlFor="cat-slug">Slug (opcional, gera automaticamente pelo nome)</Label>
            <Input
              id="cat-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              placeholder="categoria-exemplo"
            />
          </div>
          <div className="field">
            <Label htmlFor="cat-order">Order N</Label>
            <Input
              id="cat-order"
              type="number"
              value={orderN}
              onChange={(e) => setOrderN(e.target.value)}
              min={0}
            />
          </div>
          <label className="cluster" style={{ fontSize: "var(--text-base)", fontWeight: 800 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Ativa
          </label>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={mut.isPending}>
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
